import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'

import { useAppStore } from '@src/app/context'
import { ErrorBoundary } from '@src/app/error'
import config from '@src/config'
import { logError } from '@src/lib/log'

import CallQualityWizardDialog from './CallQualityWizardDialog'
import * as styles from './OngoingCalls.css'
import VoiceAgentCallDialog from './VoiceAgentCallDialog'
import { OngoingCall } from './ongoing-call'
import type CallDialogController from './store/CallDialogController'

interface OngoingCallsProps {
  className?: string
  callDialogController: CallDialogController
  onModeChange: () => void
}

const OngoingCalls = ({
  className,
  callDialogController,
  onModeChange,
}: OngoingCallsProps) => {
  const { coordinates, resetCoordinates } = callDialogController
  const { top, left, right, bottom } = coordinates
  const { electron, service, voice } = useAppStore()

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'active-calls',
  })

  const networkInformation = useMemo(() => {
    let connectionType: string = voice.networkInformationUiStore.connectionType
    if (connectionType === 'ethernet') {
      connectionType = 'wired'
    } else if (connectionType === 'wifi') {
      connectionType = 'wi_fi'
    }
    return {
      connectionType,
      effectiveConnectionType: voice.networkInformationUiStore.effectiveType,
    }
  }, [voice])

  const style = {
    ...(left < right ? { left } : { right }),
    ...(top < bottom ? { top } : { bottom }),
    transform: CSS.Transform.toString(transform),
  }

  const handleDoubleClick = () => {
    resetCoordinates()
  }

  useEffect(() => {
    electron?.app?.hasMicrophoneAccess?.()?.catch(logError)
  }, [electron])

  return (
    <>
      {service.voice.callQualityFeedbackRequest !== null ? (
        <ErrorBoundary fallback={<></>}>
          <div
            className={cx(styles.root, className)}
            style={style}
            key={service.voice.callQualityFeedbackRequest.id}
          >
            <CallQualityWizardDialog
              call={service.voice.callQualityFeedbackRequest}
              networkInformation={networkInformation}
              appVersion={config.VERSION}
            />
          </div>
        </ErrorBoundary>
      ) : null}
      {service.voice.ongoingCalls.map((call, index) => (
        <div
          ref={setNodeRef}
          className={cx(styles.root, className)}
          style={style}
          key={index}
        >
          <div
            {...attributes}
            {...listeners}
            onDoubleClick={handleDoubleClick}
            className={styles.handle}
          ></div>
          {call.isVoiceAgentTestCall ? (
            <VoiceAgentCallDialog call={call} />
          ) : (
            <OngoingCall call={call} onModeChange={onModeChange} />
          )}
        </div>
      ))}
    </>
  )
}

export default observer(OngoingCalls)
